import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { CheckCircle, XCircle, Sparkles } from 'lucide-react';

export default function SLTResponse() {
  const [responseId, setResponseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get('r') || params.get('token');
    if (!r) {
      setError('Invalid link — no question ID provided.');
      setLoading(false);
      return;
    }
    setResponseId(r);
    loadQuestion(r);
  }, []);

  async function loadQuestion(id) {
    try {
      const res = await base44.functions.invoke('recordSLTAnswer', { response_id: id });
      const data = res.data || {};
      if (data.error) {
        setError(data.error);
      } else if (data.already_answered) {
        setResult({ is_correct: data.is_correct, correct_answer: data.correct_answer, selected_answer: data.selected_answer, already: true });
      } else {
        setQuestion({ text: data.question_text, options: data.options, status: data.status, leader_name: data.leader_name });
      }
    } catch (e) {
      setError('Unable to load this question. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const res = await base44.functions.invoke('recordSLTAnswer', { response_id: responseId, selected_answer: selected });
      const data = res.data || {};
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (e) {
      setError('Unable to submit your answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111]" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-teal-700 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111] px-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-white text-lg font-semibold mb-2">Something went wrong</p>
          <p className="text-white/60 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#111111] px-4 py-8">
        <div className="max-w-lg w-full">
          <div className={`rounded-2xl p-8 border ${result.is_correct ? 'bg-teal-900/30 border-teal-700' : 'bg-amber-900/20 border-amber-700'}`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${result.is_correct ? 'bg-teal-600' : 'bg-amber-600'}`}>
              {result.is_correct ? <CheckCircle className="w-8 h-8 text-white" /> : <XCircle className="w-8 h-8 text-white" />}
            </div>
            <h1 className="text-2xl font-bold text-white text-center mb-2">
              {result.already ? 'Already Answered' : result.is_correct ? 'Correct!' : 'Not quite'}
            </h1>
            <p className="text-white/70 text-center text-sm mb-6">
              {result.is_correct
                ? 'Well done — your disability inclusion knowledge is on point.'
                : `The correct answer was: ${result.correct_answer}`}
            </p>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Your answer</p>
              <p className="text-white text-sm">{result.selected_answer}</p>
            </div>
            {result.is_correct === false && (
              <div className="bg-teal-900/30 rounded-xl p-4 border border-teal-700/50 mt-3">
                <p className="text-teal-300 text-xs uppercase tracking-wide mb-1">Correct answer</p>
                <p className="text-white text-sm">{result.correct_answer}</p>
              </div>
            )}
          </div>
          <p className="text-white/40 text-xs text-center mt-6">Your accuracy score has been updated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] px-4 py-8">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-teal-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg">Dai's Knowledge Check</h1>
            {question?.leader_name && <p className="text-white/50 text-sm">Hi {question.leader_name.split(' ')[0]}!</p>}
          </div>
        </div>

        {/* Question card */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <p className="text-white font-medium text-base mb-6">{question?.text}</p>
          <div className="space-y-3">
            {question?.options?.map((option, i) => (
              <button
                key={i}
                onClick={() => setSelected(option)}
                aria-pressed={selected === option}
                className={`w-full text-left px-4 py-3.5 rounded-xl text-sm font-medium border transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFB800] ${
                  selected === option
                    ? 'bg-teal-600 text-white border-teal-500'
                    : 'bg-white/5 text-white/80 border-white/10 hover:bg-white/10 hover:border-white/20'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    selected === option ? 'bg-white text-teal-700' : 'bg-white/10 text-white/60'
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  {option}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!selected || submitting}
          className="w-full mt-4 py-3.5 rounded-xl bg-[#FFB800] text-black font-bold text-sm hover:bg-[#FFCB1F] disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        >
          {submitting ? 'Submitting…' : 'Submit Answer'}
        </button>
        <p className="text-white/30 text-xs text-center mt-4">Powered by DI Disability Support</p>
      </div>
    </div>
  );
}